import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Camera, XCircle, Loader2, ScanText, Sparkles, Zap, CheckCircle2, AlertTriangle,
} from "lucide-react";

// Short beep + vibration copied from the barcode dialog so both flows behave the same.
let sharedAudioCtx = null;
const playBeep = () => {
  try {
    if (!sharedAudioCtx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      sharedAudioCtx = new Ctx();
    }
    const ctx = sharedAudioCtx;
    if (ctx.state === "suspended") ctx.resume();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    osc.frequency.value = 880;
    gain.gain.value = 0.001;
    osc.connect(gain);
    gain.connect(ctx.destination);
    const now = ctx.currentTime;
    gain.gain.exponentialRampToValueAtTime(0.25, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.14);
    osc.start(now);
    osc.stop(now + 0.16);
  } catch {}
};
const vibrate = (p) => { try { if (navigator.vibrate) navigator.vibrate(p); } catch {} };

/**
 * Point-your-camera-at-the-box dialog. Grabs a JPEG from the video stream, ships
 * it to `/offsite-deliveries/read-part-from-photo` and calls `onDetected(sku)`
 * with whatever the vision model reads. Uses `getUserMedia` directly (no
 * html5-qrcode) so it works reliably on iOS Safari + Android Chrome for text OCR.
 *
 * Props:
 *   - deliveryId / projectId: sent so the backend can use expected SKUs as hints
 *   - continuous: if true, capture stays open after each read for bulk scanning
 *   - onDetected(sku): parent decides what to do with the extracted SKU
 */
export default function PartNumberCameraDialog({
  open,
  onOpenChange,
  onDetected,
  deliveryId,
  projectId,
  continuous = false,
  title = "Scan part number",
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const onDetectedRef = useRef(onDetected);
  const onOpenChangeRef = useRef(onOpenChange);
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);
  const [ready, setReady] = useState(false);
  const [history, setHistory] = useState([]); // { sku, confidence, at }
  const [manual, setManual] = useState("");

  useEffect(() => {
    onDetectedRef.current = onDetected;
    onOpenChangeRef.current = onOpenChange;
  }, [onDetected, onOpenChange]);

  useEffect(() => {
    if (!open) return;
    setError("");
    setReady(false);
    setHistory([]);
    setManual("");
    let cancelled = false;
    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error("This browser does not expose the camera. Use the manual box below.");
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.playsInline = true;
          videoRef.current.muted = true;
          await videoRef.current.play();
          setReady(true);
        }
      } catch (err) {
        setError(
          err?.name === "NotAllowedError"
            ? "Camera permission was denied. Enable camera access in your browser settings and try again."
            : err?.message || "Could not open the camera."
        );
      }
    })();
    return () => {
      cancelled = true;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [open]);

  const captureFrame = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth) return null;
    // Downscale to ~1024px longest edge for faster upload
    const maxEdge = 1024;
    const scale = Math.min(1, maxEdge / Math.max(video.videoWidth, video.videoHeight));
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.85);
  };

  const doCapture = async () => {
    if (processing || !ready) return;
    const dataUrl = captureFrame();
    if (!dataUrl) {
      toast.error("Camera is not ready yet");
      return;
    }
    setProcessing(true);
    try {
      const { data } = await api.post(
        "/offsite-deliveries/read-part-from-photo",
        {
          image_base64: dataUrl,
          project_id: projectId || null,
          delivery_id: deliveryId || null,
        },
        { timeout: 30000 },
      );
      const sku = (data.sku || "").trim().toUpperCase();
      const conf = Number(data.confidence || 0);
      if (!sku) {
        setHistory((h) => [{ sku: "—", confidence: conf, at: Date.now(), status: "no-read", raw: data.raw_text }, ...h].slice(0, 8));
        toast.warning("Couldn't read a part number — hold the camera closer and try again");
        return;
      }
      playBeep();
      vibrate([60]);
      setHistory((h) => [{ sku, confidence: conf, at: Date.now(), status: "ok" }, ...h].slice(0, 8));
      onDetectedRef.current?.(sku);
      if (!continuous) {
        onOpenChangeRef.current?.(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Vision failed — try again");
    } finally {
      setProcessing(false);
    }
  };

  const submitManual = (e) => {
    e.preventDefault();
    const s = manual.trim().toUpperCase();
    if (!s) return toast.error("Enter a part number");
    onDetectedRef.current?.(s);
    setManual("");
    if (!continuous) onOpenChangeRef.current?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md max-w-lg" data-testid="part-camera-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight flex items-center gap-2">
            <ScanText size={22} /> {title}
            {continuous && (
              <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm bg-blue-100 text-blue-800">
                <Zap size={10} /> Fast mode
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="relative rounded-sm overflow-hidden border-2 border-dashed border-slate-300 bg-black aspect-[4/3]">
            <video
              ref={videoRef}
              className="absolute inset-0 w-full h-full object-cover"
              autoPlay
              playsInline
              muted
            />
            <canvas ref={canvasRef} className="hidden" />
            {!ready && !error && (
              <div className="absolute inset-0 grid place-items-center text-white/80 text-sm">
                <div className="text-center">
                  <Camera size={40} className="mx-auto" />
                  <div className="mt-2 text-xs opacity-80">Starting camera…</div>
                </div>
              </div>
            )}
            {error && (
              <div className="absolute inset-0 grid place-items-center p-6 text-center text-white bg-black/70">
                <div>
                  <XCircle size={32} className="mx-auto text-red-400" />
                  <p className="mt-2 text-sm">{error}</p>
                </div>
              </div>
            )}
            {ready && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="w-3/4 max-w-xs border-2 border-white/80 rounded-sm shadow-[0_0_0_9999px_rgba(0,0,0,0.35)] aspect-[4/1]" />
              </div>
            )}
            {ready && (
              <button
                type="button"
                onClick={doCapture}
                disabled={processing}
                data-testid="part-capture-btn"
                className="absolute bottom-3 left-1/2 -translate-x-1/2 h-14 w-14 rounded-full bg-white shadow-lg border-4 border-slate-200 grid place-items-center disabled:opacity-70"
                aria-label="Capture"
              >
                {processing ? (
                  <Loader2 size={22} className="animate-spin text-slate-800" />
                ) : (
                  <div className="h-9 w-9 rounded-full bg-blue-800" />
                )}
              </button>
            )}
          </div>
          <p className="text-xs text-slate-500">
            Point at the printed part number and tap the capture button. Hold steady — the vision
            model reads the number and auto-receives one unit.
          </p>

          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">
              Or type it in
            </div>
            <form onSubmit={submitManual} className="flex gap-2">
              <Input
                value={manual}
                onChange={(e) => setManual(e.target.value)}
                placeholder="e.g. OS-2089"
                data-testid="part-manual-input"
                className="rounded-sm font-mono uppercase"
              />
              <Button type="submit" className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm">
                Use
              </Button>
            </form>
          </div>

          {history.length > 0 && (
            <div className="border border-slate-200 rounded-sm max-h-40 overflow-y-auto">
              <div className="bg-slate-50 border-b border-slate-200 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-500 flex items-center justify-between">
                <span>Recent reads</span>
                <span className="font-mono">{history.length}</span>
              </div>
              <ul className="divide-y divide-slate-100">
                {history.map((h, i) => (
                  <li key={`${h.at}-${i}`} className="px-3 py-1.5 flex items-center justify-between text-xs">
                    <span className="font-mono font-semibold text-slate-900">{h.sku}</span>
                    <span className="inline-flex items-center gap-1 text-slate-500">
                      {h.status === "ok" ? (
                        <><CheckCircle2 size={11} className="text-emerald-600" /> {(h.confidence * 100).toFixed(0)}%</>
                      ) : (
                        <><AlertTriangle size={11} className="text-amber-600" /> no read</>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm" data-testid="part-close-btn">
            {continuous ? "Done" : "Close"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
